import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const eur = (n) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDur = (sec) => {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60), s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};
const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—");

function Stat({ label, value, tone = "" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-xl font-heading font-semibold mt-1 tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}

export default function Reports() {
  const t = useT();
  const [mode, setMode] = useState("daily");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dentists, setDentists] = useState([]);
  const [dentistId, setDentistId] = useState("all");
  const [visitRep, setVisitRep] = useState(null);

  useEffect(() => {
    api.get("/meta/dentists").then((r) => setDentists(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    if (mode === "visits") {
      const dq = dentistId && dentistId !== "all" ? `&dentist_id=${dentistId}` : "";
      api.get(`/reports/visit-durations?date=${date}${dq}`)
        .then((r) => setVisitRep(r.data)).catch(() => setVisitRep(null)).finally(() => setLoading(false));
      return;
    }
    const url = mode === "daily" ? `/reports/daily?date=${date}` : `/reports/monthly?month=${month}`;
    api.get(url).then((r) => setRep(r.data)).catch(() => setRep(null)).finally(() => setLoading(false));
  }, [mode, date, month, dentistId]);

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("reports.title")} subtitle={t("reports.subtitle")}>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
          {[["daily", t("reports.daily")], ["monthly", t("reports.monthly")], ["visits", t("reports.visits")]].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)} data-testid={`report-mode-${v}`}
              className={`px-3 py-1.5 text-sm rounded-md ${mode === v ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}>{l}</button>
          ))}
        </div>
        {mode === "monthly"
          ? <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-auto" data-testid="report-month" />
          : <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="report-date" />}
        {mode === "visits" && (
          <Select value={dentistId} onValueChange={setDentistId}>
            <SelectTrigger className="w-48" data-testid="visit-dentist-filter"><SelectValue placeholder={t("reports.dentist")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")} {t("reports.dentist")}</SelectItem>
              {dentists.map((d) => <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
        : mode === "visits" ? (
          !visitRep ? <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">{t("reports.noReport")}</div>
          : (
            <div className="space-y-6" data-testid="visit-durations-report">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label={t("reports.completedVisits")} value={visitRep.summary.completed_visits} tone="text-emerald-600" />
                <Stat label={t("reports.totalVisits")} value={visitRep.summary.total_visits} />
                <Stat label={t("reports.avgWaiting")} value={fmtDur(visitRep.summary.avg_waiting_seconds)} tone="text-amber-600" />
                <Stat label={t("reports.longestWaiting")} value={fmtDur(visitRep.summary.longest_waiting_seconds)} tone="text-red-500" />
                <Stat label={t("reports.avgTreatment")} value={fmtDur(visitRep.summary.avg_treatment_seconds)} tone="text-blue-600" />
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="visit-durations-table">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium">{t("reports.patient")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.visit")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.type")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.dentist")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.scheduled")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.arrived")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.waited")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.treatStart")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.treatDur")}</th>
                        <th className="px-3 py-2 font-medium">{t("reports.completed")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitRep.visits.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">{t("reports.noVisits")}</td></tr>}
                      {visitRep.visits.map((v) => (
                        <tr key={v.appointment_id} data-testid={`visit-row-${v.appointment_id}`} className="border-t border-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-800">{v.patient_name}</td>
                          <td className="px-3 py-2 text-slate-500">#{v.visit_number || 1}</td>
                          <td className="px-3 py-2 text-slate-500">{v.visit_type || "—"}</td>
                          <td className="px-3 py-2 text-slate-500">{v.dentist_name || "—"}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtT(v.scheduled_time)}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtT(v.arrived_at)}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtDur(v.waiting_duration_seconds)}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtT(v.treatment_started_at)}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtDur(v.treatment_duration_seconds)}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtT(v.treatment_completed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-slate-400">{t("reports.date")} {visitRep.date} · {t("reports.eachIndependent")}</p>
            </div>
          )
        )
        : !rep ? <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">{t("reports.noReport")}</div>
        : (
          <div className="space-y-6" data-testid="accounting-report">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={t("reports.revenueEur")} value={eur(rep.revenue_eur)} tone="text-emerald-600" />
              <Stat label={t("reports.cashCollected")} value={eur(rep.collections.cash_eur)} />
              <Stat label={t("reports.cardCollected")} value={eur(rep.collections.card_eur)} />
              <Stat label={t("reports.refunds")} value={eur(rep.refunds_eur)} tone="text-red-500" />
              <Stat label={t("reports.discounts")} value={eur(rep.discounts_eur)} tone="text-amber-600" />
              <Stat label={t("reports.doctorEarnings")} value={eur(rep.doctor_earnings_eur)} />
              <Stat label={t("reports.technicianEarnings")} value={eur(rep.technician_earnings_eur)} />
              <Stat label={t("reports.labCosts")} value={eur(rep.lab_costs_eur)} />
              <Stat label={t("reports.netEur")} value={eur(rep.net_eur)} tone="text-navy" />
              <Stat label={t("reports.netTry")} value={new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(rep.net_try || 0)} />
              <Stat label={t("reports.bankSettlements")} value={eur(rep.bank_settlements_eur)} />
              <Stat label={t("reports.cashBalance")} value={eur(rep.cash_balance_eur)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-heading font-medium text-navy mb-3">{t("reports.collectionsByCurrency")}</h3>
                <table className="w-full text-sm"><tbody>
                  {Object.entries(rep.by_currency).length === 0 && <tr><td className="text-slate-400 py-2">{t("reports.noPayments")}</td></tr>}
                  {Object.entries(rep.by_currency).map(([c, v]) => (
                    <tr key={c} className="border-b border-slate-50"><td className="py-2">{c}</td><td className="py-2 text-right tabular-nums">{v.amount} {c}</td><td className="py-2 text-right text-slate-400">{v.count} {t("reports.txn")}</td></tr>
                  ))}
                </tbody></table>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-heading font-medium text-navy mb-3">{t("reports.posReconciliation")}</h3>
                <p className="text-sm text-slate-600">{t("reports.cardPayments")}: <b>{rep.pos_reconciliation.card_payments}</b></p>
                <p className="text-sm text-slate-600">{t("reports.matchedPos")}: <b className="text-emerald-600">{rep.pos_reconciliation.matched_pos_reference}</b></p>
                <p className="text-sm text-slate-600">{t("reports.missingPos")}: <b className="text-amber-600" data-testid="pos-missing">{rep.pos_reconciliation.missing_pos_reference}</b></p>
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">{t("reports.discrepancies")}</p>
                  {rep.discrepancies.length === 0 ? <p className="text-sm text-emerald-600">{t("reports.noneDisc")}</p>
                    : rep.discrepancies.map((d, i) => <p key={i} className="text-sm text-amber-600">• {d}</p>)}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400">{mode === "monthly" ? `${t("reports.month")} ${rep.month}` : `${t("reports.date")} ${rep.date}`} · {t("reports.ratesToEur")}: {Object.entries(rep.exchange_rates_to_eur).map(([k, v]) => `${k} ${v}`).join(" · ")} · {t("reports.syntheticData")}</p>
          </div>
        )}
    </div>
  );
}
